import React, { useState, useEffect, useMemo } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import '../styles/pages/_consultant-group-workspace.scss';
import Button from '../components/Button';
import Modal from '../components/Modal';
import SelectDropdown from '../components/SelectDropdown';
import {
  createFolder,
  getRootFolders,
  getFolderContents,
  getArchivedItems,
  archiveFolder,
  hardDeleteFolder,
  restoreFolder
} from '../logic/groupCreation/groupFoldersApi';
import { createGroup, getMyGroups, archiveGroup, deleteGroup, restoreGroup } from '../logic/groupCreation/groupApi';

// Local data types
interface BaseItem {
  id: string;
  parentId: string | null;
  name: string;
  description: string;
  archived: boolean;
  createdAt: Date;
}

interface FolderItem extends BaseItem {
  kind: 'folder';
}

interface GroupItem extends BaseItem {
  kind: 'group';
  type: 'Student' | 'Course' | 'Faculty' | 'Year' | 'Custom Tags';
  visibility: 'Public' | 'Invite Only' | 'Code Protected';
  membersCount: number;
  pendingRequests: number;
  customTagName?: string;
  customTagColor?: string;
}

type WorkspaceItem = FolderItem | GroupItem;

interface FormData {
  kind: 'folder' | 'group';
  name: string;
  description: string;
  type?: GroupItem['type'];
  visibility?: GroupItem['visibility'];
  customTagName?: string;
  customTagColor?: string;
  // Required fields for group creation
  consentAgreed?: boolean;
}

const MAX_SUBFOLDERS_PER_FOLDER = 10;

const ConsultantGroupWorkspacePage: React.FC = () => {
  const { isDarkMode } = useTheme();
  const { user } = useAuth();
  const { folderId } = useParams<{ folderId?: string }>();
  const navigate = useNavigate();
  const isArchivedView = folderId === 'archived';

  // Protect route: Enterprise-owned consultants shouldn't access group creation
  // Also protect against Enterprise users trying to access consultant group creation
  useEffect(() => {
    if (user?.role === 'Consultant' && user?.createdBy) {
      toast.error('You do not have permission to access group creation.');
      navigate('/panel/consultant/overview', { replace: true });
    }
    if (user?.role === 'Enterprise') {
      toast.error('Enterprise users should use the Enterprise group creation panel.');
      navigate('/panel/enterprise/groups', { replace: true });
    }
  }, [user, navigate]);

  const [items, setItems] = useState<WorkspaceItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<WorkspaceItem[]>([]);
  const [breadcrumb, setBreadcrumb] = useState<Array<{ id: string, name: string }>>([]);
  const [currentFolder, setCurrentFolder] = useState<FolderItem | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<WorkspaceItem | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [hardDeleteModalOpen, setHardDeleteModalOpen] = useState(false);
  const [hardDeleteTarget, setHardDeleteTarget] = useState<FolderItem | null>(null);
  const [hardDeleteConfirmName, setHardDeleteConfirmName] = useState('');
  const [isHardDeleting, setIsHardDeleting] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    kind: 'group',
    name: '',
    description: '',
    type: 'Student',
    visibility: 'Public',
    customTagName: '',
    customTagColor: '#6366f1',
    // Required fields for group creation
    consentAgreed: false
  });
  const [formErrors, setFormErrors] = useState<{ [key: string]: string | undefined }>({});

  // Load data from backend
  const loadData = async () => {
    if (!user) return;

    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      if (isArchivedView) {
        // Load archived folders + groups (virtual folder)
        const response = await getArchivedItems(token);
        const archivedFolders = response.data.archivedFolders;
        const archivedGroups = response.data.archivedGroups;

        const folderItems: WorkspaceItem[] = archivedFolders.map(folder => ({
          id: folder.id,
          parentId: null,
          name: folder.name,
          description: folder.description,
          archived: true,
          createdAt: new Date(folder.createdAt),
          kind: 'folder' as const
        }));

        const groupItems: WorkspaceItem[] = archivedGroups.map((group: any) => ({
          id: group._id || group.id,
          parentId: group.folderId || null,
          name: group.groupName,
          description: group.description,
          archived: true,
          createdAt: new Date(group.createdAt),
          kind: 'group' as const,
          type: group.uiGroupType || 'Student',
          visibility: group.uiVisibility || 'Public',
          membersCount: group.approvedMembers || 0,
          pendingRequests: group.pendingRequests || 0,
          customTagName: group.customTagName,
          customTagColor: group.customTagColor
        }));

        const allItems = [...folderItems, ...groupItems];
        setItems(allItems);
        setFilteredItems(allItems);
        setCurrentFolder(null);
        setBreadcrumb([{ id: 'root', name: 'Groups' }, { id: 'archived', name: 'Archived' }]);
      } else if (folderId) {
        // Load folder contents
        const response = await getFolderContents(token, folderId);
        const folderData = response.data;

        setCurrentFolder({
          id: folderData.folder.id,
          parentId: null, // Will be set from breadcrumb if needed
          name: folderData.folder.name,
          description: folderData.folder.description,
          archived: false, // API doesn't provide this, assume false
          createdAt: new Date(folderData.folder.createdAt),
          kind: 'folder' as const
        });
        setBreadcrumb(folderData.breadcrumb);

        // Combine folders and groups into items
        const folderItems: WorkspaceItem[] = folderData.childFolders.map(folder => ({
          id: folder.id,
          parentId: folderId, // Current folder is parent
          name: folder.name,
          description: folder.description,
          archived: false, // API doesn't provide this, assume false
          createdAt: new Date(folder.createdAt),
          kind: 'folder' as const
        }));

        const groupItems: WorkspaceItem[] = folderData.childGroups.map(group => ({
          id: group._id || group.id,
          parentId: folderId, // Current folder is parent
          name: group.groupName,
          description: group.description,
          archived: false, // API doesn't provide this, assume false
          createdAt: new Date(group.createdAt),
          kind: 'group' as const,
          type: group.uiGroupType || 'Student',
          visibility: group.uiVisibility || 'Public',
          membersCount: group.approvedMembers || 0,
          pendingRequests: group.pendingRequests || 0,
          customTagName: group.customTagName,
          customTagColor: group.customTagColor
        }));

        const allItems = [...folderItems, ...groupItems];
        setItems(allItems);
        setFilteredItems(allItems);
      } else {
        // Load root folders and root groups
        const [foldersResponse, groupsResponse] = await Promise.all([
          getRootFolders(token),
          getMyGroups(token, 1, 100) // Get many to ensure we have root groups
        ]);

        const archivedEntry: WorkspaceItem = {
          id: 'archived',
          parentId: null,
          name: 'Archived',
          description: 'View archived folders and groups',
          archived: false,
          createdAt: new Date(0),
          kind: 'folder' as const
        };

        const folderItems: WorkspaceItem[] = foldersResponse.data.folders.map(folder => ({
          id: folder.id,
          parentId: null, // Root folders have no parent
          name: folder.name,
          description: folder.description,
          archived: false, // API doesn't provide this, assume false
          createdAt: new Date(folder.createdAt),
          kind: 'folder' as const
        }));

        // Filter groups to only those at root level (no folderId)
        const rootGroups = groupsResponse.data.groups.filter(group => !group.folderId);
        const groupItems: WorkspaceItem[] = rootGroups.map(group => ({
          id: group._id || group.id,
          parentId: null, // Root groups have no parent
          name: group.groupName,
          description: group.description,
          archived: false, // API doesn't provide this, assume false
          createdAt: new Date(group.createdAt),
          kind: 'group' as const,
          type: group.uiGroupType || 'Student',
          visibility: group.uiVisibility || 'Public',
          membersCount: group.approvedMembers || 0,
          pendingRequests: group.pendingRequests || 0,
          customTagName: group.customTagName,
          customTagColor: group.customTagColor
        }));

        const allItems = [...folderItems, ...groupItems];
        const withArchivedEntry = [archivedEntry, ...allItems];
        setItems(withArchivedEntry);
        setFilteredItems(withArchivedEntry);
        setCurrentFolder(null);
        setBreadcrumb([{ id: 'root', name: 'Groups' }]);
      }
    } catch (error: any) {

      toast.error(error.message || 'Failed to load workspace data');
    } finally {
      setIsLoading(false);
    }
  };

  // Load data on mount and when folderId changes
  useEffect(() => {
    loadData();
  }, [folderId, user]);

  // Apply search filter to loaded items
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredItems(items);
    } else {
      const filtered = items.filter(item =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredItems(filtered);
    }
  }, [items, searchTerm]);

  const subFolderCount = useMemo(
    () => items.filter(i => i.kind === 'folder' && i.id !== 'archived').length,
    [items]
  );
  const isSubFolderLimitReached = subFolderCount >= MAX_SUBFOLDERS_PER_FOLDER;

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      errors.name = `${formData.kind === 'folder' ? 'Folder' : 'Group'} name is required`;
    }

    if (formData.kind === 'group' && formData.type === 'Custom Tags' && !formData.customTagName?.trim()) {
      errors.customTagName = 'Custom tag name is required';
    }

    if (formData.kind === 'group' && !formData.consentAgreed) {
      errors.consent = 'You must agree to the consent statement';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddItem = () => {
    if (isArchivedView) {
      toast.info('You cannot create items in the Archived view');
      return;
    }
    setEditingItem(null);
    setFormData({
      kind: 'group',
      name: '',
      description: '',
      type: 'Student',
      visibility: 'Public',
      customTagName: '',
      customTagColor: '#6366f1',
      consentAgreed: false
    });
    setFormErrors({});
    setIsPanelOpen(true);
  };

  const handleEditItem = (item: WorkspaceItem) => {
    setEditingItem(item);
    if (item.kind === 'folder') {
      setFormData({
        kind: 'folder',
        name: item.name,
        description: item.description
      });
    } else {
      setFormData({
        kind: 'group',
        name: item.name,
        description: item.description,
        type: item.type,
        visibility: item.visibility,
        customTagName: item.customTagName || '',
        customTagColor: item.customTagColor || '#6366f1'
      });
    }
    setFormErrors({});
    setIsPanelOpen(true);
  };

  const handleSaveItem = async () => {
    if (!validateForm() || !user) return;

    if (formData.kind === 'folder' && isSubFolderLimitReached) {
      toast.error('Maximum 10 sub-folders allowed per folder.');
      return;
    }

    if (isArchivedView) {
      toast.info('You cannot create items in the Archived view');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Authentication required');
      return;
    }

    try {
      setIsCreating(true);

      if (editingItem) {
        // For now, we don't support editing via API (would need update endpoints)
        toast.info('Editing not yet implemented via API');
        setIsPanelOpen(false);
        setEditingItem(null);
        return;
      }

      const currentFolderId = folderId;

      if (formData.kind === 'folder') {
        // Create folder via API
        await createFolder(token, {
          name: formData.name.trim(),
          description: formData.description.trim(),
          parentFolderId: currentFolderId
        });

        toast.success('Folder created successfully');
      } else {
        // Create group via API
        const groupData = {
          groupName: formData.name.trim(),
          description: formData.description.trim(),
          tags: [],
          consultantName: user.profile?.preferredName ||
            `${user.profile?.firstName || ''} ${user.profile?.lastName || ''}`.trim() ||
            user.email || '',
          department: 'Student Wellness',
          consentAgreed: formData.consentAgreed!,
          folderId: currentFolderId,
          uiGroupType: formData.type!,
          uiVisibility: formData.visibility!,
          customTagName: formData.type === 'Custom Tags' ? formData.customTagName?.trim() : undefined,
          customTagColor: formData.type === 'Custom Tags' ? formData.customTagColor : undefined
        };

        await createGroup(token, groupData);
        toast.success('Group created successfully');
      }

      // Reload data to reflect changes
      await loadData();

      setIsPanelOpen(false);
      setEditingItem(null);
    } catch (error: any) {

      const msg = error.message || 'Failed to save item';
      const isFolderCreate = formData.kind === 'folder';
      const showMaxFolders = isFolderCreate && /too many attempts/i.test(msg);
      toast.error(showMaxFolders ? 'Maximum folders reached.' : msg);
    } finally {
      setIsCreating(false);
    }
  };

  const handleArchiveItem = async (item: WorkspaceItem) => {
    if (!user) return;

    if (item.kind === 'folder' && item.id === 'archived') {
      navigate('/panel/consultant/group-creation/archived');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Authentication required');
      return;
    }

    try {
      if (item.kind === 'folder') {
        await archiveFolder(token, item.id);
        toast.success('Folder archived');
      } else {
        await archiveGroup(token, item.id);
        toast.success('Group archived');
      }

      await loadData();
    } catch (error: any) {

      toast.error(error.message || 'Failed to archive item');
    }
  };

  const handleViewGroup = (groupId: string) => {
    navigate(`/panel/consultant/group-creation/group/${groupId}`);
  };

  const handleRestoreItem = async (item: WorkspaceItem) => {
    if (!user) return;

    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Authentication required');
      return;
    }

    try {
      if (item.kind === 'folder') {
        await restoreFolder(token, item.id);
        toast.success('Folder restored');
      } else {
        await restoreGroup(token, item.id);
        toast.success('Group restored');
      }

      await loadData();
    } catch (error: any) {

      toast.error(error.message || 'Failed to restore item');
    }
  };

  const handleDeleteGroup = async (groupId: string, groupName: string) => {
    if (!user) return;

    const ok = window.confirm(`Delete group "${groupName}"? This cannot be undone.`);
    if (!ok) return;

    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Authentication required');
      return;
    }

    try {
      setIsDeleting(true);
      await deleteGroup(token, groupId);
      toast.success('Group deleted');
      await loadData();
    } catch (error: any) {

      toast.error(error.message || 'Failed to delete group');
    } finally {
      setIsDeleting(false);
    }
  };

  const openHardDeleteFolderModal = (folder: FolderItem) => {
    setHardDeleteTarget(folder);
    setHardDeleteConfirmName('');
    setHardDeleteModalOpen(true);
  };

  const closeHardDeleteFolderModal = () => {
    if (isHardDeleting) return;
    setHardDeleteModalOpen(false);
    setHardDeleteTarget(null);
    setHardDeleteConfirmName('');
  };

  const handleHardDeleteFolder = async () => {
    if (!user || !hardDeleteTarget) return;

    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Authentication required');
      return;
    }

    try {
      setIsHardDeleting(true);
      await hardDeleteFolder(token, hardDeleteTarget.id, hardDeleteConfirmName);
      toast.success('Folder deleted');
      closeHardDeleteFolderModal();
      await loadData();
    } catch (error: any) {

      toast.error(error.message || 'Failed to delete folder');
    } finally {
      setIsHardDeleting(false);
    }
  };

  const handleCancel = () => {
    setIsPanelOpen(false);
    setEditingItem(null);
    setFormErrors({});
  };

  const handleFolderClick = (folderId: string) => {
    navigate(`/panel/consultant/group-creation/${folderId}`);
  };


  const handleBackToParent = () => {
    if (!folderId) return;

    if (folderId === 'archived') {
      navigate('/panel/consultant/group-creation');
      return;
    }

    // Use breadcrumb to find parent - breadcrumb contains path from root to current
    if (breadcrumb.length > 1) {
      // Second to last item in breadcrumb is the parent
      const parentFolder = breadcrumb[breadcrumb.length - 2];
      if (parentFolder.id === 'root') {
        navigate('/panel/consultant/group-creation');
      } else {
        navigate(`/panel/consultant/group-creation/${parentFolder.id}`);
      }
    } else {
      // No parent, go to root
      navigate('/panel/consultant/group-creation');
    }
  };

  const getItemBadgeClass = (item: WorkspaceItem): string => {
    if (item.kind === 'folder') {
      return 'consultant-group-workspace__card-badge--folder';
    }

    // For custom tags with custom colors, don't apply color classes
    if (item.type === 'Custom Tags' && item.customTagColor) {
      return 'consultant-group-workspace__card-badge';
    }

    // For groups, return a class based on type
    switch (item.type) {
      case 'Student': return 'consultant-group-workspace__card-badge--student';
      case 'Course': return 'consultant-group-workspace__card-badge--course';
      case 'Faculty': return 'consultant-group-workspace__card-badge--faculty';
      case 'Year': return 'consultant-group-workspace__card-badge--year';
      case 'Custom Tags': return 'consultant-group-workspace__card-badge--custom';
      default: return 'consultant-group-workspace__card-badge--default';
    }
  };

  const getItemBadgeStyle = (item: WorkspaceItem): React.CSSProperties | undefined => {
    if (item.kind === 'group' && item.type === 'Custom Tags' && item.customTagColor) {
      return { backgroundColor: item.customTagColor };
    }
    return undefined;
  };

  const getItemBadgeText = (item: WorkspaceItem): string => {
    if (item.kind === 'folder') return item.id === 'archived' ? 'Archived' : 'Organization';
    if (item.type === 'Custom Tags' && item.customTagName) return item.customTagName;
    return item.type;
  };

  return (
    <div className={`consultant-group-workspace ${isDarkMode ? 'dark' : ''}`}>
      {/* Top Bar */}
      <header className="consultant-group-workspace__header">
        <div className="consultant-group-workspace__header-content">
          {/* Breadcrumbs */}
          <div className="consultant-group-workspace__breadcrumbs">
            <button
              className="consultant-group-workspace__breadcrumb-link consultant-group-workspace__breadcrumb-link--button"
              onClick={() => navigate('/panel/consultant/group-creation')}
            >
              <span className="material-symbols-outlined consultant-group-workspace__breadcrumb-icon">groups</span>
              Organization
            </button>
            <span className="consultant-group-workspace__breadcrumb-separator">/</span>
            <button
              className="consultant-group-workspace__breadcrumb-link consultant-group-workspace__breadcrumb-link--button"
              onClick={() => navigate('/panel/consultant/group-creation')}
            >
              Groups
            </button>
            {breadcrumb.slice(1).map((item, index) => (
              <React.Fragment key={item.id}>
                <span className="consultant-group-workspace__breadcrumb-separator">/</span>
                {index === breadcrumb.slice(1).length - 1 ? (
                  // Last item (current folder) - non-clickable
                  <span className="consultant-group-workspace__breadcrumb-current">
                    {item.name}
                  </span>
                ) : (
                  // Ancestor items - clickable
                  <button
                    className="consultant-group-workspace__breadcrumb-link consultant-group-workspace__breadcrumb-link--button"
                    onClick={() => navigate(`/panel/consultant/group-creation/${item.id}`)}
                  >
                    {item.name}
                  </button>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Pending Requests Summary */}
          <div className="consultant-group-workspace__pending-summary">
            {(() => {
              // Calculate total pending requests and get groups with pending requests
              const groupsWithPending = items.filter(
                item => item.kind === 'group' && (item as GroupItem).pendingRequests > 0
              ) as GroupItem[];

              const totalPendingRequests = groupsWithPending.reduce(
                (sum, group) => sum + group.pendingRequests,
                0
              );

              if (totalPendingRequests === 0) {
                return null;
              }

              // Show maximum 3 groups, add "+others" if more
              const maxGroupsToShow = 3;
              const displayedGroups = groupsWithPending.slice(0, maxGroupsToShow);
              const remainingCount = groupsWithPending.length - maxGroupsToShow;

              const groupNames = displayedGroups.map(g => g.name).join(', ') +
                (remainingCount > 0 ? ` +${remainingCount} others` : '');

              const firstGroupId = groupsWithPending[0].id;

              return (
                <button
                  className="consultant-group-workspace__pending-info"
                  onClick={() => handleViewGroup(firstGroupId)}
                  title={`Click to view ${groupsWithPending[0].name}`}
                >
                  <span className="material-symbols-outlined">mail</span>
                  <span>{totalPendingRequests} Pending Request{totalPendingRequests !== 1 ? 's' : ''}</span>
                  <span className="consultant-group-workspace__pending-separator">•</span>
                  <span className="consultant-group-workspace__pending-groups-inline">
                    {groupNames}
                  </span>
                </button>
              );
            })()}
          </div>

          {/* Search & Actions */}
          <div className="consultant-group-workspace__actions">
            <div className="consultant-group-workspace__search">
              <span className="material-symbols-outlined consultant-group-workspace__search-icon">search</span>
              <input
                type="text"
                className="consultant-group-workspace__search-input"
                placeholder={folderId ? "Search in this folder" : "Search groups"}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Button
              className="consultant-group-workspace__add-btn"
              onClick={handleAddItem}
              disabled={isArchivedView}
            >
              <span className="material-symbols-outlined">add</span>
              <span>Add {folderId ? 'Item' : 'Folder/Group'}</span>
            </Button>
            <button className="consultant-group-workspace__settings-btn">
              <span className="material-symbols-outlined">settings</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="consultant-group-workspace__main">
        <div className="consultant-group-workspace__content">
          {isLoading ? (
            <div className="consultant-group-workspace__loading">
              <div className="consultant-group-workspace__spinner"></div>
              <span>Loading workspace...</span>
            </div>
          ) : (
            <>
              <div className="consultant-group-workspace__content-header">
                <h2 className="consultant-group-workspace__title">
                  {isArchivedView ? 'Archived' : folderId ? `${currentFolder?.name || 'Folder'}` : 'Creation Workspace'}
                </h2>
                <p className="consultant-group-workspace__subtitle">
                  {isArchivedView
                    ? 'Archived folders and groups. You can permanentlyF delete items here.'
                    : folderId
                      ? 'Organize folders and groups within this container'
                      : 'Create folders and groups for your wellness programs'}
                </p>
                {folderId && (
                  <button
                    className="consultant-group-workspace__back-btn"
                    onClick={handleBackToParent}
                  >
                    <span className="material-symbols-outlined">arrow_back</span>
                    Back to Parent
                  </button>
                )}
              </div>

              <div className="consultant-group-workspace__grid">
                {filteredItems.map(item => (
                  (() => {
                    const isArchivedEntry = item.kind === 'folder' && item.id === 'archived';
                    const showActions = !isArchivedEntry;
                    const canNavigateToFolder = item.kind === 'folder' && !isArchivedView;

                    const cardIcon = isArchivedEntry ? 'archive' : item.kind === 'folder' ? 'corporate_fare' : 'groups';

                    const cardMeta = isArchivedEntry
                      ? { icon: 'inventory_2', text: 'Archived Items' }
                      : item.kind === 'folder'
                        ? { icon: 'inventory_2', text: 'Container' }
                        : {
                          icon: 'groups',
                          text: `${(item as GroupItem).membersCount} Members • ${(item as GroupItem).pendingRequests} Requests`
                        };

                    return (
                      <div
                        key={item.id}
                        className={[
                          'consultant-group-workspace__card',
                          item.kind === 'folder' && !isArchivedView ? 'consultant-group-workspace__card--folder' : '',
                          isArchivedEntry ? 'consultant-group-workspace__card--archived-entry' : ''
                        ].filter(Boolean).join(' ')}
                        onClick={canNavigateToFolder ? () => handleFolderClick(item.id) : undefined}
                      >
                        <div className="consultant-group-workspace__card-header">
                          <div className="consultant-group-workspace__card-icon">
                            <span className="material-symbols-outlined">
                              {cardIcon}
                            </span>
                          </div>
                          <span
                            className={`consultant-group-workspace__card-badge ${getItemBadgeClass(item)}`}
                            style={getItemBadgeStyle(item)}
                          >
                            {getItemBadgeText(item)}
                          </span>
                        </div>
                        <h3 className="consultant-group-workspace__card-title">{item.name}</h3>
                        <div className="consultant-group-workspace__card-meta">
                          <span className="material-symbols-outlined">{cardMeta.icon}</span>
                          <span>{cardMeta.text}</span>
                        </div>

                        {/* Hover Actions */}
                        {showActions && (
                          <div className="consultant-group-workspace__card-actions">
                            {!isArchivedView && (
                              <button
                                className="consultant-group-workspace__card-action-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditItem(item);
                                }}
                                title="Edit"
                              >
                                <span className="material-symbols-outlined">edit</span>
                              </button>
                            )}
                            {!isArchivedView && item.kind !== 'folder' && (
                              <button
                                className="consultant-group-workspace__card-action-btn consultant-group-workspace__card-action-btn--view"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewGroup(item.id);
                                }}
                                title="View"
                              >
                                <span className="material-symbols-outlined">visibility</span>
                              </button>
                            )}
                            {!isArchivedView && (
                              <button
                                className="consultant-group-workspace__card-action-btn consultant-group-workspace__card-action-btn--archive"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleArchiveItem(item);
                                }}
                                title="Archive"
                              >
                                <span className="material-symbols-outlined">archive</span>
                              </button>
                            )}
                            {isArchivedView && (
                              <button
                                className="consultant-group-workspace__card-action-btn consultant-group-workspace__card-action-btn--archive"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRestoreItem(item);
                                }}
                                title="Restore"
                              >
                                <span className="material-symbols-outlined">unarchive</span>
                              </button>
                            )}
                            <button
                              className="consultant-group-workspace__card-action-btn consultant-group-workspace__card-action-btn--danger"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (item.kind === 'folder') {
                                  openHardDeleteFolderModal(item);
                                } else {
                                  handleDeleteGroup(item.id, item.name);
                                }
                              }}
                              title="Delete"
                              disabled={isDeleting || isHardDeleting}
                            >
                              <span className="material-symbols-outlined">delete</span>
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })()
                ))}

                {filteredItems.length === 0 && (
                  <div className="consultant-group-workspace__empty">
                    <span className="material-symbols-outlined consultant-group-workspace__empty-icon">
                      {folderId ? 'folder_off' : 'inventory_2'}
                    </span>
                    <p className="consultant-group-workspace__empty-text">
                      {searchTerm
                        ? `No items found matching "${searchTerm}"`
                        : folderId
                          ? 'This folder is empty. Add folders or groups to organize content.'
                          : 'No groups created yet. Start by creating your first group or folder!'
                      }
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </main>

      {/* Side Panel */}
      <aside className={`consultant-group-workspace__panel ${isPanelOpen ? 'consultant-group-workspace__panel--open' : ''}`}>
        <div className="consultant-group-workspace__panel-header">
          <div>
            <h2 className="consultant-group-workspace__panel-title">
              {editingItem ? `Edit ${editingItem.kind === 'folder' ? 'Folder' : 'Group'}` : 'Add New Item'}
            </h2>
            <p className="consultant-group-workspace__panel-subtitle">
              {folderId ? `Inside: ${items.find(item => item.id === folderId)?.name}` : 'Organization Creation'}
            </p>
          </div>
          <button
            className="consultant-group-workspace__panel-close"
            onClick={handleCancel}
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <div className="consultant-group-workspace__panel-content">
          {/* Item Type Selector */}
          {!editingItem && (
            <div className="consultant-group-workspace__field">
              <label className="consultant-group-workspace__field-label">
                <span className="material-symbols-outlined consultant-group-workspace__field-icon">category</span>
                Item Type *
              </label>
              <div className="consultant-group-workspace__type-selector">
                <label className={`consultant-group-workspace__type-option ${isSubFolderLimitReached ? 'consultant-group-workspace__type-option--disabled' : ''}`}>
                  <input
                    type="radio"
                    name="kind"
                    value="folder"
                    checked={formData.kind === 'folder'}
                    onChange={(e) => handleInputChange('kind', e.target.value as 'folder' | 'group')}
                    className="consultant-group-workspace__radio"
                    disabled={isSubFolderLimitReached}
                  />
                  <div className="consultant-group-workspace__type-content">
                    <span className="material-symbols-outlined">folder</span>
                    <div>
                      <span className="consultant-group-workspace__type-title">Folder</span>
                      <span className="consultant-group-workspace__type-description">Container for organizing groups</span>
                    </div>
                  </div>
                </label>
                <label className="consultant-group-workspace__type-option">
                  <input
                    type="radio"
                    name="kind"
                    value="group"
                    checked={formData.kind === 'group'}
                    onChange={(e) => handleInputChange('kind', e.target.value as 'folder' | 'group')}
                    className="consultant-group-workspace__radio"
                  />
                  <div className="consultant-group-workspace__type-content">
                    <span className="material-symbols-outlined">groups</span>
                    <div>
                      <span className="consultant-group-workspace__type-title">Group</span>
                      <span className="consultant-group-workspace__type-description">Student collaboration group</span>
                    </div>
                  </div>
                </label>
              </div>
              {isSubFolderLimitReached && (
                <p className="consultant-group-workspace__subfolder-limit-hint">
                  Maximum 10 sub-folders per folder. Create a group or use another folder.
                </p>
              )}
            </div>
          )}

          {/* Item Name */}
          <div className="consultant-group-workspace__field">
            <label className="consultant-group-workspace__field-label">
              <span className="material-symbols-outlined consultant-group-workspace__field-icon">edit_note</span>
              {formData.kind === 'folder' ? 'Folder' : 'Group'} Name *
            </label>
            <input
              type="text"
              className={`form-input ${formErrors.name ? 'consultant-group-workspace__input--error' : ''}`}
              placeholder="e.g. Advanced Calculus"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
            />
            {formErrors.name && <span className="consultant-group-workspace__error">{formErrors.name}</span>}
          </div>

          {/* Description */}
          <div className="consultant-group-workspace__field">
            <label className="consultant-group-workspace__field-label">
              <span className="material-symbols-outlined consultant-group-workspace__field-icon">description</span>
              Description
            </label>
            <textarea
              className="form-textarea"
              placeholder="Describe the purpose of this group..."
              rows={3}
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
            />
          </div>

          {/* Group-specific fields (only show for groups) */}
          {formData.kind === 'group' && (
            <>
              {/* Group Type */}
              <div className="consultant-group-workspace__field">
                <label className="consultant-group-workspace__field-label">
                  <span className="material-symbols-outlined consultant-group-workspace__field-icon">label</span>
                  Group Type
                </label>
                <SelectDropdown
                  value={formData.type || ''}
                  onChange={(value) => handleInputChange('type', value as GroupItem['type'])}
                  options={[
                    { value: 'Student', label: 'Student' },
                    { value: 'Course', label: 'Course' },
                    { value: 'Faculty', label: 'Faculty' },
                    { value: 'Year', label: 'Year' },
                    { value: 'Custom Tags', label: 'Custom Tags' }
                  ]}
                />
              </div>

              {/* Custom Tag Fields (only show when Custom Tags is selected) */}
              {formData.type === 'Custom Tags' && (
                <>
                  {/* Custom Tag Name */}
                  <div className="consultant-group-workspace__field">
                    <label className="consultant-group-workspace__field-label">
                      <span className="material-symbols-outlined consultant-group-workspace__field-icon">edit</span>
                      Custom Tag Name *
                    </label>
                    <input
                      type="text"
                      className={`form-input ${formErrors.customTagName ? 'consultant-group-workspace__input--error' : ''}`}
                      placeholder="e.g. Wellness Champions"
                      value={formData.customTagName}
                      onChange={(e) => handleInputChange('customTagName', e.target.value)}
                    />
                    {formErrors.customTagName && <span className="consultant-group-workspace__error">{formErrors.customTagName}</span>}
                  </div>

                  {/* Custom Tag Color */}
                  <div className="consultant-group-workspace__field">
                    <label className="consultant-group-workspace__field-label">
                      <span className="material-symbols-outlined consultant-group-workspace__field-icon">palette</span>
                      Tag Color
                    </label>
                    <div className="consultant-group-workspace__color-picker">
                      <input
                        type="color"
                        className="consultant-group-workspace__color-input"
                        value={formData.customTagColor}
                        onChange={(e) => handleInputChange('customTagColor', e.target.value)}
                      />
                      <span className="consultant-group-workspace__color-preview"
                        style={{ backgroundColor: formData.customTagColor }}></span>
                      <span className="consultant-group-workspace__color-value">{formData.customTagColor}</span>
                    </div>
                  </div>
                </>
              )}

              {/* Visibility */}
              <div className="consultant-group-workspace__field">
                <label className="consultant-group-workspace__field-label">
                  <span className="material-symbols-outlined consultant-group-workspace__field-icon">visibility</span>
                  Visibility
                </label>
                <div className="consultant-group-workspace__visibility-options">
                  <label className="consultant-group-workspace__visibility-option">
                    <input
                      type="radio"
                      name="visibility"
                      value="Public"
                      checked={formData.visibility === 'Public'}
                      onChange={(e) => handleInputChange('visibility', e.target.value as GroupItem['visibility'])}
                      className="consultant-group-workspace__radio"
                    />
                    <div className="consultant-group-workspace__visibility-content">
                      <span className="consultant-group-workspace__visibility-title">Public</span>
                      <span className="consultant-group-workspace__visibility-description">Anyone in the workspace can view</span>
                    </div>
                  </label>
                  <label className="consultant-group-workspace__visibility-option">
                    <input
                      type="radio"
                      name="visibility"
                      value="Invite Only"
                      checked={formData.visibility === 'Invite Only'}
                      onChange={(e) => handleInputChange('visibility', e.target.value as GroupItem['visibility'])}
                      className="consultant-group-workspace__radio"
                    />
                    <div className="consultant-group-workspace__visibility-content">
                      <span className="consultant-group-workspace__visibility-title">Invite Only</span>
                      <span className="consultant-group-workspace__visibility-description">Only members you manually add</span>
                    </div>
                  </label>
                  <label className="consultant-group-workspace__visibility-option">
                    <input
                      type="radio"
                      name="visibility"
                      value="Code Protected"
                      checked={formData.visibility === 'Code Protected'}
                      onChange={(e) => handleInputChange('visibility', e.target.value as GroupItem['visibility'])}
                      className="consultant-group-workspace__radio"
                    />
                    <div className="consultant-group-workspace__visibility-content">
                      <span className="consultant-group-workspace__visibility-title">Code Protected</span>
                      <span className="consultant-group-workspace__visibility-description">Requires a specific access code</span>
                    </div>
                  </label>
                </div>
              </div>
            </>
          )}

          {/* Consent checkbox for groups */}
          {formData.kind === 'group' && (
            <div className="consultant-group-workspace__consent">
              <label className="consultant-group-workspace__consent-label">
                <input
                  type="checkbox"
                  checked={formData.consentAgreed || false}
                  onChange={(e) => setFormData(prev => ({ ...prev, consentAgreed: e.target.checked }))}
                  className="consultant-group-workspace__consent-checkbox"
                />
                <span className="consultant-group-workspace__consent-text">
                  I agree to the data management policies and understand that this group will be visible to students for joining.
                </span>
              </label>
              {formErrors.consent && <span className="consultant-group-workspace__error">{formErrors.consent}</span>}
            </div>
          )}
        </div>

        <div className="consultant-group-workspace__panel-footer">
          <Button
            variant="primary"
            onClick={handleSaveItem}
            disabled={isCreating}
            className="consultant-group-workspace__save-btn"
          >
            <span className="material-symbols-outlined">check_circle</span>
            <span>
              {isCreating
                ? 'Creating...'
                : editingItem
                  ? `Update ${editingItem.kind === 'folder' ? 'Folder' : 'Group'}`
                  : `Create ${formData.kind === 'folder' ? 'Folder' : 'Group'}`
              }
            </span>
          </Button>
        </div>
      </aside>

      <Modal
        isOpen={hardDeleteModalOpen}
        title={hardDeleteTarget ? `Hard delete folder "${hardDeleteTarget.name}"?` : 'Hard delete folder?'}
        onClose={closeHardDeleteFolderModal}
        actionLabel="Hard Delete"
        onAction={handleHardDeleteFolder}
        actionDisabled={
          !hardDeleteTarget ||
          hardDeleteConfirmName !== hardDeleteTarget.name ||
          isHardDeleting
        }
        className="consultant-group-workspace__hard-delete-modal"
      >
        <p className="consultant-group-workspace__hard-delete-text">
          This will permanently delete the folder and all descendant folders and groups. To confirm, type the folder name exactly:
        </p>
        <input
          type="text"
          className="form-input consultant-group-workspace__confirm-input"
          placeholder={hardDeleteTarget?.name || ''}
          value={hardDeleteConfirmName}
          onChange={(e) => setHardDeleteConfirmName(e.target.value)}
          disabled={isHardDeleting}
        />
        <p className="consultant-group-workspace__hard-delete-hint">
          Confirmation required: <span className="consultant-group-workspace__hard-delete-name">{hardDeleteTarget?.name}</span>
        </p>
      </Modal>
    </div>
  );
};

export default ConsultantGroupWorkspacePage;
